/**
 * Rank-share links are outward-facing (posted to X), so they always point at
 * the canonical public site — never the local/preview origin the sharer
 * happens to be browsing on, and never with a `/demo-mode` path prefix. The
 * backend verifies shares against this host (`isOwnShareUrl`), so the two
 * must stay in lockstep.
 */
export const SHARE_ORIGIN = 'https://morfi.markets';

export interface RankShareInput {
  rank: number | null;
  points: string;
  handle: string | null;
  levelKey: string | null;
  levelName: string | null;
}

/**
 * The crawlable rank-share landing URL (`/rewards?rrank=…`) whose OG
 * metadata unfurls this participant's rank card (Case-B piggy-back — see
 * `lib/seo/surfaces/rewards.ts`, same params as the profile-card share).
 * Empty string until a real rank exists.
 */
export function buildRankShareUrl(input: RankShareInput): string {
  if (input.rank === null || input.rank <= 0) return '';
  const u = new URL('/rewards', SHARE_ORIGIN);
  u.searchParams.set('rrank', String(input.rank));
  u.searchParams.set('rpts', input.points);
  u.searchParams.set('rh', input.handle ?? 'anon');
  u.searchParams.set('rlvl', input.levelKey ?? 'spectator');
  u.searchParams.set('rname', input.levelName ?? '');
  return u.toString();
}

/**
 * The X compose intent the share quest's "Do it" button opens — posts the
 * rank-share URL with friendly text. The backend verifies the quest by finding
 * a tweet linking that page (`rewards` + `rrank=`) in the user's timeline.
 */
export function buildRankShareIntent(shareUrl: string, text: string): string {
  if (shareUrl === '') return '';
  return `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
}
